import api from './api';

/**
 * Intake form service - handles intake form submissions
 */

/**
 * Get intake form questions for a product
 */
export const getIntakeForm = async (productId, options = {}) => {
  const params = options.preview ? `?preview=${options.preview}` : '';
  const response = await api.get(`/intake-forms/product/${productId}${params}`);
  return response.form;
};

/**
 * Submit intake form answers
 */
export const submitIntakeForm = async (productId, data) => {
  const formData = new FormData();

  // Add answers as JSON
  formData.append('answers', JSON.stringify(data.answers));

  // Add license files (front and back) if uploaded
  if (data.licenseFront) {
    formData.append('licenseFront', data.licenseFront);
  }
  if (data.licenseBack) {
    formData.append('licenseBack', data.licenseBack);
  }

  // TRT recent-lab document (card 1254). Its own field name, deliberately: the server
  // files it as a CLINICAL document (`trt_lab_results`), not as an identity document,
  // and the two must stay distinguishable in `submission.documents`.
  if (data.labResults) {
    formData.append('labResults', data.labResults);
  }

  // Optional prior/existing prescription (card 28374). Its own field name so the server
  // files it as `prior_prescription` rather than as an identity or a lab document; all
  // three must stay distinguishable in `submission.documents`.
  if (data.priorPrescription) {
    formData.append('priorPrescription', data.priorPrescription);
  }

  // If using existing documents (no new uploads)
  if (data.useExistingDocuments) {
    formData.append('useExistingDocuments', 'true');
  }

  // Consent data
  if (data.intakeConsentAgreed) {
    formData.append('intakeConsentAgreed', 'true');
    formData.append('intakeConsentTimestamp', data.intakeConsentTimestamp || new Date().toISOString());
  }

  const previewQuery = data.preview ? `?preview=${data.preview}` : '';
  const response = await api.upload(`/intake-forms/product/${productId}/submit${previewQuery}`, formData);

  return response.submission;
};

/**
 * Attach a lab document to this user's EXISTING submission for a product (card 7237).
 *
 * A second `submitIntakeForm` call would not do: `intakeSubmissionRepository.create`
 * INSERTs, so re-posting the form to carry one extra file would leave the patient with
 * two submission rows for one intake. This endpoint appends the document to the row
 * that already exists instead.
 *
 * The field name is `labResults` deliberately — the same one the intake route accepts —
 * so the server files it as a CLINICAL document (`trt_lab_results`), distinguishable
 * from identity documents, and the shared `DOCUMENT_MIME_TYPES` allowlist applies.
 */
export const submitLabResults = async (productId, file) => {
  const formData = new FormData();
  formData.append('labResults', file);

  const response = await api.upload(`/intake-forms/product/${productId}/lab-results`, formData);

  return response.submission;
};

/**
 * Get user's intake submission for a product
 */
export const getMySubmission = async (productId) => {
  const response = await api.get(`/intake-forms/product/${productId}/my-submission`);
  return response.submission;
};

/**
 * Get all user's intake submissions
 */
export const getMySubmissions = async () => {
  const response = await api.get('/intake-forms/my-submissions');
  return response.submissions || [];
};

/**
 * Get user's existing documents (from previous submissions)
 */
export const getMyDocuments = async () => {
  const response = await api.get('/intake-forms/my-documents');
  return response.documents;
};

export default {
  getIntakeForm,
  submitIntakeForm,
  submitLabResults,
  getMySubmission,
  getMySubmissions,
  getMyDocuments
};

